import React, { useEffect, useState } from 'react';
import type {
  ChangeEvent,
  ChangeEventHandler,
  FocusEventHandler,
  FormEvent,
  ReactNode,
} from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Stack,
  TextField,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  AddCircleOutline,
  CheckCircleOutline,
  Close,
  CloudUploadOutlined,
  DescriptionOutlined,
  EditOutlined,
  VisibilityOutlined,
} from '@mui/icons-material';
import { getCEP } from '../../../../services/patients/patientsService';
import type { PatientV2CompanyOption } from '../../../../services/patients/patientsV2Service';
import { BrighterGreen, DarkGreen } from '../../../../app/theme/tokens/colors';
import type {
  PatientDetailsPatch,
  PatientV2ClinicalData,
  PatientV2Detail,
  PatientV2Document,
  ReviewSectionKey,
  ReviewSectionStatus,
} from './types';
import { formatCPF } from '../../../../utils/format';
import { PatientDateInput } from './PatientDateInput';
import {
  brazilianStates,
  crmParts,
  databaseDate,
  dateError,
  decimalError,
  isValidCpf,
  normalizePatientForm,
} from './PatientFormValidation';

const predefinedDocumentTypes = [
  'Prescrição médica atualizada',
  'Laudo médico com CID',
  'Gasometria arterial',
  'Radiografia de tórax',
  'Comprovante de endereço',
  'Documento de identidade',
  'Cartão do SUS (CNS)',
  'Termo de responsabilidade',
];

const documentTypeOptions = [...predefinedDocumentTypes, 'Outro'];

type ValidationField =
  | 'name'
  | 'cpf'
  | 'cellphone'
  | 'phoneNumber'
  | 'cep'
  | 'city'
  | 'uf'
  | 'address'
  | 'number'
  | 'companyId'
  | 'birthDate'
  | 'requestingDoctorCrmNumber'
  | 'requestingDoctorCrmUf'
  | 'email'
  | 'oxygenFlow'
  | 'dailyUse'
  | 'requestDate';
type PatientFormField =
  | keyof PatientV2Detail
  | 'name'
  | 'requestingDoctorCrmNumber'
  | 'requestingDoctorCrmUf';
const validationFields: ValidationField[] = [
  'name',
  'cpf',
  'cellphone',
  'phoneNumber',
  'cep',
  'city',
  'uf',
  'address',
  'number',
  'companyId',
  'birthDate',
  'requestingDoctorCrmNumber',
  'requestingDoctorCrmUf',
  'email',
  'oxygenFlow',
  'dailyUse',
  'requestDate',
];
const clinicalValidationFields = [
  'requestingDoctorCrmNumber',
  'requestingDoctorCrmUf',
  'oxygenFlow',
  'dailyUse',
  'requestDate',
];

const isValidationField = (field: string): field is ValidationField =>
  validationFields.includes(field as ValidationField);

interface EditableSectionProps {
  number: string;
  title: string;
  children: ReactNode;
  first?: boolean;
  reviewMode?: boolean;
  sectionStatus?: ReviewSectionStatus;
  onConfirm?: () => void;
}

interface FormGridProps {
  children: ReactNode;
  columns?: number;
}

type InputProps = Omit<
  React.ComponentProps<typeof TextField>,
  'label' | 'value' | 'onChange' | 'onBlur' | 'error' | 'helperText'
> & {
  label: string;
  value?: string;
  onChange?: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onBlur?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  error?: string | boolean;
  helperText?: ReactNode;
  required?: boolean;
};

interface PreviewFile {
  name: string;
  type: string;
  url: string;
}

export interface PatientDocumentReplacementFile {
  replacedDocumentId: number;
  file: PatientDocumentUploadFile;
}

export type PatientDocumentUploadFile = File & {
  documentType?: string;
  customDocumentType?: string;
  documentGeneratedAt?: string;
};

interface DocumentSlot {
  key: string;
  documentType: string;
  customDocumentType: string;
  documentGeneratedAt: string;
  file?: PatientDocumentUploadFile;
  document?: PatientV2Document;
}

const formatFileSize = (size: number): string => {
  if (!size) return 'Tamanho não informado';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const slotLabel = (slot: DocumentSlot): string =>
  slot.documentType === 'Outro'
    ? slot.customDocumentType.trim() || 'Outro documento'
    : slot.documentType || 'Documento sem tipo';

// Documentos persistidos ocupam o slot do seu tipo; os demais viram slots extras.
const buildDocumentSlots = (documents: PatientV2Document[]): DocumentSlot[] => {
  const unmatched = [...documents];
  const slots = predefinedDocumentTypes.map<DocumentSlot>((documentType) => {
    const index = unmatched.findIndex(
      (document) => document.type === documentType
    );
    const document = index >= 0 ? unmatched.splice(index, 1)[0] : undefined;
    return {
      key: `type:${documentType}`,
      documentType,
      customDocumentType: '',
      documentGeneratedAt: document?.documentGeneratedAt ?? '',
      document,
    };
  });
  unmatched.forEach((document) => {
    slots.push({
      key: `doc:${document.id}`,
      documentType: 'Outro',
      customDocumentType:
        document.type && document.type !== 'Não informado' ? document.type : '',
      documentGeneratedAt: document.documentGeneratedAt ?? '',
      document,
    });
  });
  return slots;
};

interface EmptyDocumentSlotProps {
  slot: DocumentSlot;
  onAttach: (event: ChangeEvent<HTMLInputElement>) => void;
  removable: boolean;
  onRemove: () => void;
}

const EmptyDocumentSlot = ({
  slot,
  onAttach,
  removable,
  onRemove,
}: EmptyDocumentSlotProps) => {
  const label = slotLabel(slot);

  return (
    <Box
      component="label"
      sx={{
        minHeight: 80,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        border: '1px dashed #b9c7bc',
        borderRadius: 1.5,
        backgroundColor: '#fff',
        cursor: 'pointer',
        transition: 'border-color 160ms ease, background-color 160ms ease',
        '&:hover': { borderColor: BrighterGreen, backgroundColor: '#f7fbf8' },
      }}
    >
      <input
        hidden
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        onChange={onAttach}
      />
      {removable && (
        <IconButton
          size="small"
          aria-label={`Remover ${label}`}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onRemove();
          }}
          sx={{ position: 'absolute', top: 5, right: 5, color: '#66736a' }}
        >
          <Close fontSize="small" />
        </IconButton>
      )}
      <DescriptionOutlined sx={{ color: BrighterGreen, fontSize: 28 }} />
      <Typography
        variant="body2"
        sx={{ fontWeight: 700, lineHeight: 1.35, maxWidth: 190 }}
      >
        {label}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontSize: '0.7rem' }}
      >
        Clique para inserir documento
      </Typography>
    </Box>
  );
};

interface AddedDocumentCardProps {
  slot: DocumentSlot;
  editable: boolean;
  removable: boolean;
  onAttach: (event: ChangeEvent<HTMLInputElement>) => void;
  onClear: () => void;
  onRemove: () => void;
  onPreview: () => void;
  onEditType: () => void;
  onDateChange: (value: string) => void;
  submitted: boolean;
}

const AddedDocumentCard = ({
  slot,
  editable,
  removable,
  onAttach,
  onClear,
  onRemove,
  onPreview,
  onEditType,
  onDateChange,
  submitted,
}: AddedDocumentCardProps) => {
  const label = slotLabel(slot);
  const fileDescription = slot.file
    ? `${slot.file.name} · ${formatFileSize(slot.file.size)}`
    : slot.document
      ? `${slot.document.name} · ${slot.document.size}`
      : '';

  return (
    <Stack
      spacing={1}
      sx={{
        p: 1.25,
        border: '1px solid #dce4de',
        borderRadius: 1.5,
        backgroundColor: '#fff',
        minHeight: 158,
        boxShadow: '0 1px 3px rgba(35, 58, 42, 0.06)',
      }}
    >
      <Stack
        direction="row"
        spacing={0.75}
        alignItems="flex-start"
        sx={{ minHeight: 32 }}
      >
        <DescriptionOutlined sx={{ color: BrighterGreen, mt: 0.1 }} />
        <Typography
          variant="body2"
          sx={{
            minWidth: 0,
            flex: 1,
            fontWeight: 700,
            lineHeight: 1.35,
            overflowWrap: 'anywhere',
          }}
        >
          {label}
        </Typography>
        {editable && (
          <IconButton
            size="small"
            aria-label={`Alterar tipo de ${label}`}
            onClick={onEditType}
          >
            <EditOutlined fontSize="small" />
          </IconButton>
        )}
      </Stack>

      <Typography
        variant="caption"
        sx={{ color: '#48554c', fontWeight: 600, fontSize: '0.7rem' }}
      >
        Data do documento
      </Typography>
      <PatientDateInput
        label={`Data do documento ${label}`}
        value={slot.documentGeneratedAt}
        onChange={onDateChange}
        disabled={!editable}
        submitted={submitted}
      />

      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        sx={{ mt: 'auto', minWidth: 0 }}
      >
        <DescriptionOutlined sx={{ color: '#526158', fontSize: 18 }} />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            minWidth: 0,
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={fileDescription}
        >
          {fileDescription}
        </Typography>
        {slot.file && (
          <IconButton
            size="small"
            aria-label={`Visualizar ${slot.file.name}`}
            onClick={onPreview}
          >
            <VisibilityOutlined fontSize="small" />
          </IconButton>
        )}
        {slot.file ? (
          <IconButton
            size="small"
            aria-label={`Remover arquivo de ${label}`}
            onClick={onClear}
          >
            <Close fontSize="small" />
          </IconButton>
        ) : removable ? (
          <IconButton
            size="small"
            aria-label={`Remover ${label}`}
            onClick={onRemove}
          >
            <Close fontSize="small" />
          </IconButton>
        ) : null}
      </Stack>

      {editable && slot.document && !slot.file && (
        <Button
          component="label"
          size="small"
          startIcon={<CloudUploadOutlined />}
          sx={{
            alignSelf: 'flex-start',
            textTransform: 'none',
            color: BrighterGreen,
            fontWeight: 700,
          }}
        >
          Substituir arquivo
          <input
            hidden
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={onAttach}
          />
        </Button>
      )}
    </Stack>
  );
};

interface PatientDetailsFormProps {
  patient: PatientV2Detail;
  formId?: string;
  onChange: (patch: PatientDetailsPatch) => void;
  onSubmit?: (
    patient: PatientV2Detail,
    documents: PatientDocumentUploadFile[],
    documentReplacements: PatientDocumentReplacementFile[]
  ) => void;
  reviewMode?: boolean;
  reviewSectionStatus?: Partial<Record<ReviewSectionKey, ReviewSectionStatus>>;
  onConfirmSection?: (section: ReviewSectionKey) => void;
  patientOnly?: boolean;
  showClinical?: boolean;
  showDocuments?: boolean;
  showSap?: boolean;
  sapLoading?: boolean;
  sapError?: string;
  companyOptions?: PatientV2CompanyOption[];
  companyLoading?: boolean;
  onSapChange?: (sap: string) => void;
  onCompanyChange?: (company: PatientV2CompanyOption | null) => void;
}

const onlyDigits = (value: unknown): string =>
  String(value ?? '').replace(/\D/g, '');
const normalizeSap = (value: unknown): string =>
  onlyDigits(value).replace(/^0+/, '');
const isEnterpriseSap = (value: unknown): boolean => {
  const sap = normalizeSap(value);
  return sap.startsWith('1') || sap.startsWith('6');
};
const isEmpty = (value: unknown): boolean => String(value ?? '').trim() === '';
const isValidPhone = (value: unknown): boolean => {
  const digits = onlyDigits(value);
  return (
    /^[+\d\s().-]+$/.test(String(value ?? '')) &&
    digits.length >= 10 &&
    digits.length <= 12
  );
};
const isValidCep = (value: unknown): boolean =>
  /^\d{5}-?\d{3}$/.test(String(value ?? '').trim());
const formSections = {
  display: {
    borderTop: '1px solid #bdcbbf',
    pt: { xs: 2, md: 2.5 },
    mt: { xs: 2, md: 2.5 },
  },
};

export const EditableSection = ({
  number,
  title,
  children,
  first = false,
  reviewMode = false,
  sectionStatus,
  onConfirm,
}: EditableSectionProps) => (
  <Box sx={first ? { pt: 0 } : formSections.display}>
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      alignItems={{ xs: 'stretch', sm: 'center' }}
      justifyContent="space-between"
      spacing={1}
      sx={{ mb: 2 }}
    >
      <Typography
        variant="subtitle1"
        sx={{ color: DarkGreen, fontWeight: 700 }}
      >
        {number}. {title}
      </Typography>
      {reviewMode &&
        (sectionStatus === 'confirmed' ? (
          <Chip
            icon={<CheckCircleOutline />}
            label="Seção conferida"
            size="small"
            sx={{
              alignSelf: { xs: 'flex-start', sm: 'auto' },
              color: '#287a20',
              backgroundColor: '#eaf6e6',
              fontWeight: 700,
            }}
          />
        ) : (
          <Button
            size="small"
            variant="outlined"
            startIcon={<CheckCircleOutline />}
            onClick={onConfirm}
            sx={{
              alignSelf: { xs: 'flex-start', sm: 'auto' },
              color: DarkGreen,
              borderColor: '#9eb7a2',
              textTransform: 'none',
              fontWeight: 700,
            }}
          >
            Confirmar seção
          </Button>
        ))}
    </Stack>
    {children}
  </Box>
);

const FormGrid = ({ children, columns = 4 }: FormGridProps) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: {
        xs: '1fr',
        sm: `repeat(${Math.min(columns, 2)}, minmax(0, 1fr))`,
        md: `repeat(${columns}, minmax(0, 1fr))`,
      },
      gap: { xs: 1.5, md: 2 },
    }}
  >
    {children}
  </Box>
);

const Input = ({
  label,
  value,
  onChange,
  onBlur,
  error,
  helperText,
  required = false,
  ...props
}: InputProps) => (
  <TextField
    fullWidth
    size="small"
    label={required ? `${label} *` : label}
    value={value ?? ''}
    onChange={onChange}
    onBlur={onBlur}
    error={Boolean(error)}
    helperText={error || helperText}
    {...props}
  />
);

export const PatientDetailsForm = ({
  patient,
  formId,
  onChange,
  onSubmit,
  reviewMode = false,
  reviewSectionStatus = {},
  onConfirmSection,
  patientOnly = false,
  showClinical = true,
  showDocuments = true,
  showSap = true,
  sapLoading = false,
  sapError = '',
  companyOptions = [],
  companyLoading = false,
  onSapChange,
  onCompanyChange,
}: PatientDetailsFormProps) => {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [cpfEditing, setCpfEditing] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const formRef = React.useRef<HTMLFormElement>(null);
  const cepRequest = React.useRef(0);
  const latestPatient = React.useRef(patient);
  latestPatient.current = patient;
  useEffect(
    () => () => {
      cepRequest.current++;
    },
    []
  );
  const [cepLoading, setCepLoading] = useState(false);
  const [cepError, setCepError] = useState('');
  const [documentSlots, setDocumentSlots] = useState<DocumentSlot[]>(() =>
    buildDocumentSlots(patient?.documents ?? [])
  );
  const [typeDescriptionTouched, setTypeDescriptionTouched] = useState(false);
  const [typeEditorKey, setTypeEditorKey] = useState<string | null>(null);
  const [typeDraft, setTypeDraft] = useState({
    documentType: '',
    customDocumentType: '',
  });
  const [documentError, setDocumentError] = useState('');
  const [preview, setPreview] = useState<PreviewFile | null>(null);

  // Recria os slots apenas quando os documentos persistidos realmente mudam, preservando os arquivos anexados.
  const persistedDocumentsKey = (patient?.documents ?? [])
    .map(
      (document) =>
        `${document.id}:${document.type}:${document.documentGeneratedAt ?? ''}`
    )
    .join('|');
  useEffect(() => {
    setDocumentSlots(buildDocumentSlots(patient?.documents ?? []));
  }, [persistedDocumentsKey]);

  useEffect(
    () => () => {
      if (preview?.url) URL.revokeObjectURL(preview.url);
    },
    [preview]
  );

  const setField = (field: PatientFormField, value: string) => {
    onChange({ [field]: value } as PatientDetailsPatch);
    if (touched[field] && isValidationField(field))
      setErrors((current) => ({
        ...current,
        [field]: validateField(field, value),
      }));
  };
  const setClinicalField = (
    field: keyof PatientV2ClinicalData,
    value: string
  ) => {
    const next = {
      ...patient.clinical,
      ...crmParts(patient.clinical),
      [field]: value,
    };
    onChange({ clinical: { [field]: value } });
    clinicalValidationFields.forEach((key) => {
      if (touched[key])
        setErrors((current) => ({
          ...current,
          [key]: validateField(
            key as ValidationField,
            next[key as keyof PatientV2ClinicalData],
            next
          ),
        }));
    });
  };

  const validateField = (
    field: ValidationField,
    value?: unknown,
    clinical = patient.clinical
  ): string => {
    const fieldValue =
      value ??
      (clinicalValidationFields.includes(field)
        ? { ...clinical, ...crmParts(clinical) }[
        field as keyof PatientV2ClinicalData
        ]
        : field === 'name'
          ? patient?.patient
          : patient?.[field as keyof PatientV2Detail]);

    switch (field) {
      case 'name':
        return isEmpty(fieldValue)
          ? 'Informe o nome completo do paciente.'
          : '';
      case 'cpf':
        if (isEmpty(fieldValue) && isEnterpriseSap(patient?.sap)) return '';
        if (isEmpty(fieldValue)) return 'Informe o CPF do paciente.';
        return isValidCpf(fieldValue)
          ? ''
          : 'Informe um CPF válido com 11 dígitos.';
      case 'phoneNumber':
        if (isEmpty(fieldValue))
          return 'Informe o telefone/celular do paciente.';
        return isValidPhone(fieldValue)
          ? ''
          : 'Telefone/celular deve conter DDD e ter entre 10 e 12 dígitos.';
      case 'cep':
        if (isEmpty(fieldValue)) return 'Informe o CEP.';
        return isValidCep(fieldValue) ? '' : 'CEP deve conter 8 dígitos.';
      case 'city':
        return isEmpty(fieldValue) ? 'Informe a cidade.' : '';
      case 'uf':
        if (isEmpty(fieldValue)) return 'Informe a UF.';
        return brazilianStates.includes(String(fieldValue).trim().toUpperCase())
          ? ''
          : 'Selecione uma UF brasileira válida.';
      case 'address':
        return isEmpty(fieldValue) ? 'Informe o endereço.' : '';
      case 'number':
        return isEmpty(fieldValue) ? 'Informe o número.' : '';
      case 'companyId':
        return Number.isSafeInteger(Number(fieldValue)) &&
          Number(fieldValue) > 0
          ? ''
          : 'Selecione a empresa/CNPJ.';
      case 'birthDate':
      case 'requestDate':
        return dateError(fieldValue);
      case 'email':
        return isEmpty(fieldValue) ||
          /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(fieldValue).trim())
          ? ''
          : 'Informe um e-mail válido.';
      case 'oxygenFlow':
        return decimalError(fieldValue, 999.99);
      case 'dailyUse':
        return decimalError(fieldValue, 24);
      case 'requestingDoctorCrmNumber': {
        const parts = crmParts(clinical);
        if (!parts.requestingDoctorCrmUf && !parts.requestingDoctorCrmNumber)
          return '';
        return /^\d+$/.test(parts.requestingDoctorCrmNumber)
          ? ''
          : 'Informe o número do CRM, apenas com dígitos.';
      }
      case 'requestingDoctorCrmUf': {
        const parts = crmParts(clinical);
        if (!parts.requestingDoctorCrmUf && !parts.requestingDoctorCrmNumber)
          return '';
        return brazilianStates.includes(
          parts.requestingDoctorCrmUf.replace(/^CRM-/, '')
        )
          ? ''
          : 'Selecione a UF do CRM.';
      }
      default:
        return '';
    }
  };

  const touchField = (field: ValidationField) => {
    setTouched((current) => ({ ...current, [field]: true }));
    setErrors((current) => ({ ...current, [field]: validateField(field) }));
  };

  const changeField =
    (field: PatientFormField) =>
      (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value =
          field === 'cpf'
            ? onlyDigits(event.target.value).slice(0, 11)
            : field === 'uf'
              ? event.target.value.toUpperCase()
              : event.target.value;
        setField(field, value);
        if (touched[field] && isValidationField(field))
          setErrors((current) => ({
            ...current,
            [field]: validateField(field, value),
          }));
      };

  const lookupCep = async () => {
    const requestedCep = onlyDigits(patient.cep);
    if (requestedCep.length !== 8) return;
    const requestId = ++cepRequest.current;
    const before = { ...patient };
    setCepError('');
    setCepLoading(true);
    try {
      const response = await getCEP(requestedCep);
      if (
        requestId !== cepRequest.current ||
        onlyDigits(latestPatient.current.cep) !== requestedCep
      )
        return;
      const address = response?.body ?? response;
      if (!address || address.erro) {
        setCepError(
          'CEP fornecido não está ligado a nenhum endereço. Preencha o formulário com o endereço desejado.'
        );
        return;
      }

      const patch: PatientDetailsPatch = {};
      const fields = {
        address: address.logradouro,
        neighborhood: address.bairro,
        city: address.localidade,
        uf: address.uf,
      };
      Object.entries(fields).forEach(([key, value]) => {
        const field = key as 'address' | 'neighborhood' | 'city' | 'uf';
        if (value && latestPatient.current[field] === before[field])
          patch[field] = value;
      });
      onChange(patch);
      setErrors((current) => {
        const next = { ...current };
        for (const [field, value] of Object.entries(patch)) {
          if (touched[field])
            next[field] = validateField(field as ValidationField, value);
        }
        return next;
      });
    } catch {
      if (
        requestId !== cepRequest.current ||
        onlyDigits(latestPatient.current.cep) !== requestedCep
      )
        return;
      setCepError(
        'Não foi possível consultar o CEP. Preencha o endereço manualmente.'
      );
    } finally {
      if (requestId === cepRequest.current) setCepLoading(false);
    }
  };

  const validateAll = (): Record<string, string> => {
    const nextErrors = validationFields.reduce<Record<string, string>>(
      (current, field) => {
        const message =
          !showClinical && clinicalValidationFields.includes(field)
            ? ''
            : validateField(field);
        if (message) current[field] = message;
        return current;
      },
      {}
    );
    setTouched(
      validationFields.reduce<Record<string, boolean>>(
        (current, field) => ({ ...current, [field]: true }),
        {}
      )
    );
    setErrors(nextErrors);
    return nextErrors;
  };

  const toUploadFile = (slot: DocumentSlot): PatientDocumentUploadFile =>
    Object.assign(slot.file as PatientDocumentUploadFile, {
      documentType: slot.documentType,
      customDocumentType:
        slot.documentType === 'Outro' ? slot.customDocumentType.trim() : '',
      documentGeneratedAt: databaseDate(slot.documentGeneratedAt),
    }) as PatientDocumentUploadFile;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitted(true);
    setDocumentError('');
    const nextErrors = validateAll();
    const filledSlots = documentSlots.filter((slot) => slot.file);
    const focusInvalid = () =>
      window.setTimeout(() => {
        formRef.current
          ?.querySelector<HTMLElement>('[aria-invalid="true"]')
          ?.focus();
      }, 0);
    const invalidDocumentDate = filledSlots.some((slot) =>
      dateError(slot.documentGeneratedAt)
    );
    if (invalidDocumentDate) {
      focusInvalid();
      return;
    }
    if (
      filledSlots.some((slot) => slot.customDocumentType.trim().length > 100)
    ) {
      setDocumentError('A descrição do documento deve ter até 100 caracteres.');
      return;
    }
    if (
      filledSlots.some(
        (slot) =>
          slot.documentType === 'Outro' && !slot.customDocumentType.trim()
      )
    ) {
      setDocumentError('Informe o tipo do documento selecionado como Outro.');
      return;
    }
    const chosenDocumentTypes = documentSlots
      .filter((slot) => slot.file || slot.document)
      .map((slot) => slot.documentType)
      .filter((type) => predefinedDocumentTypes.includes(type));
    if (new Set(chosenDocumentTypes).size !== chosenDocumentTypes.length) {
      setDocumentError(
        'Cada tipo de documento predefinido pode ser selecionado apenas uma vez.'
      );
      return;
    }
    if (Object.keys(nextErrors).length > 0) {
      focusInvalid();
      return;
    }
    setDocumentError('');

    onSubmit?.(
      normalizePatientForm(patient),
      filledSlots.filter((slot) => !slot.document).map(toUploadFile),
      filledSlots
        .filter((slot) => slot.document)
        .map((slot) => ({
          replacedDocumentId: Number(slot.document?.id),
          file: toUploadFile(slot),
        }))
    );
  };

  const updateSlot = (key: string, patch: Partial<DocumentSlot>) =>
    setDocumentSlots((current) =>
      current.map((slot) => (slot.key === key ? { ...slot, ...patch } : slot))
    );

  const attachFileToSlot =
    (key: string) => (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;

      const supportedTypes = new Set([
        'application/pdf',
        'image/jpeg',
        'image/png',
      ]);
      if (!supportedTypes.has(file.type) || file.size > 10 * 1024 * 1024) {
        setDocumentError(
          'Envie apenas arquivos PDF, JPG, JPEG ou PNG com até 10 MB cada.'
        );
        return;
      }
      const totalSize =
        documentSlots.reduce(
          (total, slot) =>
            total + (slot.key === key ? 0 : slot.file?.size ?? 0),
          0
        ) + file.size;
      if (totalSize > 30 * 1024 * 1024) {
        setDocumentError('O total de documentos não pode ultrapassar 30 MB.');
        return;
      }
      setDocumentError('');
      updateSlot(key, { file: file as PatientDocumentUploadFile });
    };

  const clearSlotFile = (key: string) => {
    setDocumentError('');
    setDocumentSlots((current) =>
      current.map((slot) =>
        slot.key === key ? { ...slot, file: undefined } : slot
      )
    );
  };

  const removeSlot = (key: string) =>
    setDocumentSlots((current) => current.filter((slot) => slot.key !== key));

  const addDocumentSlot = () => {
    setTypeDescriptionTouched(false);
    const key = `extra:${Date.now()}-${documentSlots.length}`;
    setDocumentSlots((current) => [
      ...current,
      {
        key,
        documentType: 'Outro',
        customDocumentType: '',
        documentGeneratedAt: '',
      },
    ]);
    setTypeDraft({ documentType: 'Outro', customDocumentType: '' });
    setTypeEditorKey(key);
  };

  const openTypeEditor = (slot: DocumentSlot) => {
    setTypeDescriptionTouched(false);
    setTypeDraft({
      documentType: slot.documentType,
      customDocumentType: slot.customDocumentType,
    });
    setTypeEditorKey(slot.key);
  };

  const closeTypeEditor = () => setTypeEditorKey(null);

  const saveTypeEditor = () => {
    setTypeDescriptionTouched(true);
    if (!typeEditorKey) return;
    if (
      typeDraft.documentType === 'Outro' &&
      (!typeDraft.customDocumentType.trim() ||
        typeDraft.customDocumentType.trim().length > 100)
    )
      return;
    setDocumentError('');
    const isPredefined = predefinedDocumentTypes.includes(
      typeDraft.documentType
    );
    setDocumentSlots((current) =>
      current
        .filter(
          (slot) =>
            !(
              isPredefined &&
              slot.key !== typeEditorKey &&
              slot.documentType === typeDraft.documentType &&
              !slot.file &&
              !slot.document
            )
        )
        .map((slot) =>
          slot.key === typeEditorKey
            ? {
              ...slot,
              documentType: typeDraft.documentType,
              customDocumentType:
                typeDraft.documentType === 'Outro'
                  ? typeDraft.customDocumentType.trim()
                  : '',
            }
            : slot
        )
    );
    setTypeEditorKey(null);
  };

  // Um tipo predefinido só bloqueia a lista quando o slot correspondente já tem arquivo.
  const usedDocumentTypesExcept = (key: string | null) =>
    new Set(
      documentSlots
        .filter(
          (slot) =>
            slot.key !== key &&
            (slot.file || slot.document) &&
            predefinedDocumentTypes.includes(slot.documentType)
        )
        .map((slot) => slot.documentType)
    );

  const previewFile = (file: File) => {
    if (!file) return;
    setPreview({
      name: file.name,
      type: file.type || '',
      url: URL.createObjectURL(file),
    });
  };

  const closePreview = () => setPreview(null);

  const isSlotEditable = (slot: DocumentSlot) =>
    patientOnly
      ? true
      : Boolean(slot.document && slot.document.status === 'Pendente');
  const usedTypesInEditor = usedDocumentTypesExcept(typeEditorKey);

  return (
    <Box
      component="form"
      ref={formRef}
      id={formId}
      onSubmit={handleSubmit}
      noValidate
    >
      {Object.keys(errors).some((field) => errors[field]) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Corrija os campos destacados antes de salvar.
        </Alert>
      )}
      {cepError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {cepError}
        </Alert>
      )}
      {documentError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {documentError}
        </Alert>
      )}

      <EditableSection
        number="1"
        title="Dados do paciente"
        first
        reviewMode={reviewMode}
        sectionStatus={reviewSectionStatus.patient}
        onConfirm={() => onConfirmSection?.('patient')}
      >
        <Stack spacing={2}>
          <FormGrid columns={4}>
            <Input
              label="Nome completo"
              inputProps={{ maxLength: 255 }}
              placeholder="Digite o nome completo"
              value={patient?.patient}
              onChange={(event) => {
                onChange({
                  patient: event.target.value,
                  name: event.target.value,
                } as PatientDetailsPatch);
                if (touched.name)
                  setErrors((current) => ({
                    ...current,
                    name: validateField('name', event.target.value),
                  }));
              }}
              onBlur={() => touchField('name')}
              error={touched.name && errors.name}
              required
            />
            {!patientOnly && (
              <Input
                label="CNS"
                placeholder="Digite o CNS"
                value={patient?.cns}
                onChange={changeField('cns')}
              />
            )}
            {showSap && (
              <Input
                label="SAP"
                placeholder="Código SAP"
                value={patient?.sap}
                disabled
                helperText={
                  sapLoading ? 'Consultando vínculo SAP...' : sapError
                }
                error={sapError}
              />
            )}
            <Input
              label="CPF"
              inputProps={{ maxLength: 14, inputMode: 'numeric' }}
              placeholder="Digite o CPF"
              value={
                cpfEditing ? onlyDigits(patient?.cpf) : formatCPF(patient?.cpf)
              }
              onFocus={() => setCpfEditing(true)}
              onChange={changeField('cpf')}
              onBlur={() => {
                setCpfEditing(false);
                touchField('cpf');
              }}
              error={touched.cpf && errors.cpf}
              required={!isEnterpriseSap(patient?.sap)}
            />
            <PatientDateInput
              label="Data de nascimento"
              value={patient?.birthDate ?? ''}
              onChange={(value) => setField('birthDate', value)}
              onBlur={() => touchField('birthDate')}
              submitted={submitted}
            />
            <Input
              label="E-mail (opcional)"
              inputProps={{ maxLength: 255 }}
              placeholder="Digite o e-mail"
              value={patient?.email}
              onChange={changeField('email')}
              onBlur={() => touchField('email')}
              error={touched.email && errors.email}
            />
            <Input
              label="Telefone (com DDD)"
              inputProps={{ maxLength: 20 }}
              placeholder="(00) 00000-0000"
              value={patient?.phoneNumber}
              onChange={changeField('phoneNumber')}
              onBlur={() => touchField('phoneNumber')}
              error={touched.phoneNumber && errors.phoneNumber}
              required
            />
            <Autocomplete
              options={companyOptions}
              loading={companyLoading}
              value={
                companyOptions.find(
                  (option) => option.id === patient?.companyId
                ) ?? null
              }
              getOptionLabel={(option) =>
                option.companyName
                  ? `${option.companyName} — ${option.cnpj}`
                  : option.cnpj
              }
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_, option) => {
                onCompanyChange?.(option);
                if (touched.companyId)
                  setErrors((current) => ({
                    ...current,
                    companyId: validateField('companyId', option?.id ?? 0),
                  }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  size="small"
                  label="Empresa / CNPJ *"
                  placeholder="Buscar por empresa ou CNPJ..."
                  onBlur={() => touchField('companyId')}
                  error={Boolean(
                    (touched.companyId && errors.companyId) || sapError
                  )}
                  helperText={
                    (touched.companyId && errors.companyId) || sapError
                  }
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {companyLoading ? (
                          <CircularProgress color="inherit" size={16} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </FormGrid>
          <FormGrid columns={4}>
            {!patientOnly && (
              <>
                <Input
                  label="URS solicitante"
                  placeholder="Informe a URS solicitante"
                  value={patient?.urs}
                  onChange={changeField('urs')}
                />
                <Input
                  label="STS"
                  placeholder="Informe o STS"
                  value={patient?.sts}
                  onChange={changeField('sts')}
                />
                <Input
                  label="CRS"
                  placeholder="Informe o CRS"
                  value={patient?.crs}
                  onChange={changeField('crs')}
                />
              </>
            )}
          </FormGrid>
          <FormGrid columns={4}>
            <Input
              label="CEP"
              inputProps={{ maxLength: 9 }}
              placeholder="00000-000"
              value={patient?.cep}
              onChange={(event) => {
                cepRequest.current++;
                setCepLoading(false);
                setCepError('');
                changeField('cep')(event);
              }}
              onBlur={() => {
                touchField('cep');
                void lookupCep();
              }}
              error={touched.cep && errors.cep}
              helperText={cepLoading ? 'Consultando CEP...' : ''}
              required
            />
            <Input
              label="Cidade"
              inputProps={{ maxLength: 255 }}
              placeholder="Informe a cidade"
              value={patient?.city}
              onChange={changeField('city')}
              onBlur={() => touchField('city')}
              error={touched.city && errors.city}
              required
            />
            <Input
              label="UF"
              placeholder="UF"
              value={patient?.uf}
              onChange={changeField('uf')}
              onBlur={() => touchField('uf')}
              error={touched.uf && errors.uf}
              required
              inputProps={{ maxLength: 2 }}
            />
            <Input
              label="Número"
              inputProps={{ maxLength: 20 }}
              placeholder="Número"
              value={patient?.number}
              onChange={changeField('number')}
              onBlur={() => touchField('number')}
              error={touched.number && errors.number}
              required
            />
            <Input
              label="Endereço"
              inputProps={{ maxLength: 255 }}
              placeholder="Rua, avenida ou logradouro"
              value={patient?.address}
              onChange={changeField('address')}
              onBlur={() => touchField('address')}
              error={touched.address && errors.address}
              required
            />
            <Input
              label="Bairro"
              inputProps={{ maxLength: 255 }}
              placeholder="Informe o bairro"
              value={patient?.neighborhood}
              onChange={changeField('neighborhood')}
            />
            <Input
              label="Complemento (opcional)"
              inputProps={{ maxLength: 255 }}
              placeholder="Apartamento, bloco, casa..."
              value={patient?.addressLine2}
              onChange={changeField('addressLine2')}
            />
          </FormGrid>
        </Stack>
      </EditableSection>

      {showClinical && (
        <>
          <EditableSection
            number="2"
            title="Informações clínicas"
            reviewMode={reviewMode}
            sectionStatus={reviewSectionStatus.clinical}
            onConfirm={() => onConfirmSection?.('clinical')}
          >
            <Stack spacing={2}>
              <FormGrid columns={4}>
                <Input
                  label="CID principal"
                  inputProps={{ maxLength: 255 }}
                  placeholder="Informe o CID principal"
                  value={patient?.clinical?.cidPrimary}
                  onChange={(event) =>
                    setClinicalField('cidPrimary', event.target.value)
                  }
                />
                <Input
                  label="CID secundário"
                  inputProps={{ maxLength: 255 }}
                  placeholder="Informe o CID secundário"
                  value={patient?.clinical?.cidSecondary}
                  onChange={(event) =>
                    setClinicalField('cidSecondary', event.target.value)
                  }
                />
                <Input
                  label="Diagnóstico clínico"
                  placeholder="Descreva o diagnóstico"
                  value={patient?.clinical?.diagnosis}
                  onChange={(event) =>
                    setClinicalField('diagnosis', event.target.value)
                  }
                />
                <Input
                  label="Indicação de O₂"
                  placeholder="Descreva a indicação"
                  value={patient?.clinical?.oxygenIndication}
                  onChange={(event) =>
                    setClinicalField('oxygenIndication', event.target.value)
                  }
                />
              </FormGrid>
              <FormGrid columns={4}>
                <Input
                  label="Fonte de O₂ prescrita"
                  inputProps={{ maxLength: 255 }}
                  placeholder="Informe a fonte de O₂"
                  value={patient?.clinical?.oxygenSource}
                  onChange={(event) =>
                    setClinicalField('oxygenSource', event.target.value)
                  }
                />
                <Input
                  label="Fluxo (L/min)"
                  onBlur={() => touchField('oxygenFlow')}
                  error={touched.oxygenFlow && errors.oxygenFlow}
                  inputProps={{ inputMode: 'decimal' }}
                  placeholder="Ex.: 2,0"
                  value={patient?.clinical?.oxygenFlow}
                  onChange={(event) =>
                    setClinicalField('oxygenFlow', event.target.value)
                  }
                />
                <Input
                  label="Horas de uso/dia"
                  onBlur={() => touchField('dailyUse')}
                  error={touched.dailyUse && errors.dailyUse}
                  inputProps={{ inputMode: 'decimal' }}
                  placeholder="Ex.: 15,0"
                  value={patient?.clinical?.dailyUse}
                  onChange={(event) =>
                    setClinicalField('dailyUse', event.target.value)
                  }
                />
                <Input
                  label="Equipamento solicitado"
                  inputProps={{ maxLength: 255 }}
                  placeholder="Informe o equipamento"
                  value={patient?.clinical?.requestedEquipment}
                  onChange={(event) =>
                    setClinicalField('requestedEquipment', event.target.value)
                  }
                />
              </FormGrid>
              <FormGrid columns={4}>
                <Input
                  label="Médico solicitante"
                  inputProps={{ maxLength: 255 }}
                  placeholder="Informe o médico solicitante"
                  value={patient?.clinical?.requestingDoctorName}
                  onChange={(event) =>
                    setClinicalField('requestingDoctorName', event.target.value)
                  }
                />
                <Autocomplete
                  options={[
                    'CRM-AC',
                    'CRM-AL',
                    'CRM-AM',
                    'CRM-AP',
                    'CRM-BA',
                    'CRM-CE',
                    'CRM-DF',
                    'CRM-ES',
                    'CRM-GO',
                    'CRM-MA',
                    'CRM-MG',
                    'CRM-MS',
                    'CRM-MT',
                    'CRM-PA',
                    'CRM-PB',
                    'CRM-PE',
                    'CRM-PI',
                    'CRM-PR',
                    'CRM-RJ',
                    'CRM-RN',
                    'CRM-RO',
                    'CRM-RR',
                    'CRM-RS',
                    'CRM-SC',
                    'CRM-SE',
                    'CRM-SP',
                    'CRM-TO',
                  ]}
                  value={
                    crmParts(patient.clinical).requestingDoctorCrmUf || null
                  }
                  onChange={(_, value) =>
                    setClinicalField(
                      'requestingDoctorCrmUf' as keyof PatientV2ClinicalData,
                      value ?? ''
                    )
                  }
                  onBlur={() => touchField('requestingDoctorCrmUf')}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="CRM do médico"
                      error={Boolean(
                        touched.requestingDoctorCrmUf &&
                        errors.requestingDoctorCrmUf
                      )}
                      helperText={
                        touched.requestingDoctorCrmUf &&
                        errors.requestingDoctorCrmUf
                      }
                      size="small"
                      placeholder="Selecione o CRM"
                    />
                  )}
                />
                <Input
                  label="Número CRM"
                  inputProps={{ maxLength: 248, inputMode: 'numeric' }}
                  placeholder="Informe o número do CRM"
                  value={crmParts(patient.clinical).requestingDoctorCrmNumber}
                  onChange={(event) =>
                    setClinicalField(
                      'requestingDoctorCrmNumber' as keyof PatientV2ClinicalData,
                      event.target.value.replace(/\D/g, '')
                    )
                  }
                  onBlur={() => touchField('requestingDoctorCrmNumber')}
                  error={
                    touched.requestingDoctorCrmNumber &&
                    errors.requestingDoctorCrmNumber
                  }
                />

                <PatientDateInput
                  label="Data da solicitação"
                  value={patient?.clinical?.requestDate ?? ''}
                  onChange={(value) => setClinicalField('requestDate', value)}
                  onBlur={() => touchField('requestDate')}
                  submitted={submitted}
                />
              </FormGrid>
            </Stack>
          </EditableSection>
        </>
      )}

      {showDocuments && (
        <EditableSection
          number="3"
          title="Documentos"
          reviewMode={reviewMode}
          sectionStatus={reviewSectionStatus.documents}
          onConfirm={() => onConfirmSection?.('documents')}
        >
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.25 }}>
            Envie os documentos solicitados. Você pode adicionar arquivos em
            PDF, JPG, JPEG ou PNG (até 10 MB por arquivo).
          </Typography>

          <Box
            sx={{
              maxHeight: 410,
              overflowY: 'auto',
              pr: { sm: 0.75 },
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                md: 'repeat(3, minmax(0, 1fr))',
                xl: 'repeat(4, minmax(0, 1fr))',
              },
              gap: 1.5,
            }}
          >
            {documentSlots.map((slot) =>
              slot.file || slot.document ? (
                <AddedDocumentCard
                  key={slot.key}
                  slot={slot}
                  editable={isSlotEditable(slot)}
                  removable={
                    patientOnly &&
                    slot.key.startsWith('extra:') &&
                    !slot.document
                  }
                  onAttach={attachFileToSlot(slot.key)}
                  onClear={() => clearSlotFile(slot.key)}
                  onRemove={() => removeSlot(slot.key)}
                  onPreview={() => slot.file && previewFile(slot.file)}
                  onEditType={() => openTypeEditor(slot)}
                  submitted={submitted}
                  onDateChange={(value) =>
                    updateSlot(slot.key, { documentGeneratedAt: value })
                  }
                />
              ) : (
                <EmptyDocumentSlot
                  key={slot.key}
                  slot={slot}
                  removable={patientOnly && slot.key.startsWith('extra:')}
                  onAttach={attachFileToSlot(slot.key)}
                  onRemove={() => removeSlot(slot.key)}
                />
              )
            )}
            {patientOnly && (
              <Box
                component="button"
                type="button"
                onClick={addDocumentSlot}
                sx={{
                  minHeight: 80,
                  p: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',

                  textAlign: 'center',
                  border: '1px dashed #b9c7bc',
                  borderRadius: 1.5,
                  color: DarkGreen,
                  backgroundColor: '#fff',
                  cursor: 'pointer',
                  transition:
                    'border-color 160ms ease, background-color 160ms ease',
                  '&:hover': {
                    borderColor: BrighterGreen,
                    backgroundColor: '#f7fbf8',
                  },
                }}
              >
                <AddCircleOutline sx={{ color: BrighterGreen, fontSize: 28 }} />
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 700, lineHeight: 1.35 }}
                >
                  Adicionar outro documento
                </Typography>
              </Box>
            )}
          </Box>
        </EditableSection>
      )}

      <Dialog
        open={Boolean(typeEditorKey)}
        onClose={closeTypeEditor}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Tipo do documento</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5} sx={{ pt: 0.5 }}>
            <TextField
              select
              label="Categoria"
              size="small"
              fullWidth
              value={typeDraft.documentType}
              onChange={(event) =>
                setTypeDraft({
                  documentType: event.target.value,
                  customDocumentType:
                    event.target.value === 'Outro'
                      ? typeDraft.customDocumentType
                      : '',
                })
              }
            >
              {documentTypeOptions.map((option) => (
                <MenuItem
                  key={option}
                  value={option}
                  disabled={option !== 'Outro' && usedTypesInEditor.has(option)}
                >
                  {option}
                </MenuItem>
              ))}
            </TextField>
            {typeDraft.documentType === 'Outro' && (
              <TextField
                label="Descreva o tipo do documento"
                inputProps={{ maxLength: 100 }}
                onBlur={() => setTypeDescriptionTouched(true)}
                error={
                  typeDescriptionTouched && !typeDraft.customDocumentType.trim()
                }
                helperText={
                  typeDescriptionTouched && !typeDraft.customDocumentType.trim()
                    ? 'Informe a descrição do documento.'
                    : ''
                }
                size="small"
                fullWidth
                required
                placeholder="Ex.: Relatório de fisioterapia"
                value={typeDraft.customDocumentType}
                onChange={(event) =>
                  setTypeDraft((current) => ({
                    ...current,
                    customDocumentType: event.target.value,
                  }))
                }
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeTypeEditor} sx={{ textTransform: 'none' }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={
              !typeDraft.documentType ||
              (typeDraft.documentType === 'Outro' &&
                !typeDraft.customDocumentType.trim())
            }
            onClick={saveTypeEditor}
            sx={{
              textTransform: 'none',
              backgroundColor: BrighterGreen,
              '&:hover': { backgroundColor: DarkGreen },
            }}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(preview)}
        onClose={closePreview}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {preview?.name || 'Visualização do documento'}
          <IconButton aria-label="Fechar visualização" onClick={closePreview}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            minHeight: 300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {preview?.type.startsWith('image/') ? (
            <Box
              component="img"
              src={preview.url}
              alt={preview.name}
              sx={{ maxWidth: '100%', maxHeight: '65vh', objectFit: 'contain' }}
            />
          ) : preview?.type === 'application/pdf' ? (
            <Box
              component="iframe"
              title={preview.name}
              src={preview.url}
              sx={{ width: '100%', height: '65vh', border: 0 }}
            />
          ) : (
            <Typography color="text.secondary">
              Pré-visualização disponível para imagens e PDF.
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};
